import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING, ADD, EDIT, OPEN, ASC,
} from "const"
import { OkrObjective, OkrKeyResult } from "server/models"
import { StateType, hasAccess } from "client/utils"
import { sort } from "client/store/utils"
import {
    PRM_OKR_OBJECTIVE_KEY_RESULT,
    StoreType,
    OkrObjectiveProps,
    ObjectiveNested,
    KeyResultNested,

    LoadAllOkrSessions,
    LoadAllOkrObjectives,
    LoadAllOkrKeyResults,
    LoadOkrObjectiveById,
    SortOkrSessions,
    PrepareOkrNested,
    IsOwnerOkrObjective,

    setOkrObjectiveAvailableSessions,
    setOkrObjectiveAvailableObjectives,
    setOkrObjectiveAvailableKeyResults,
    setOkrObjectiveEdit,
    setOkrObjectiveReloadEdit,
    initOkrObjective,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import NotFound from "client/parts/notFound"
import Form from "./form"

const ObjectiveEdit = ({
    sessionId = null,
    objectiveId = null,
    search,
}: OkrObjectiveProps) => {
    const session = useSelector((state: StoreType) => state.okrSession.sessionView)
    const objectiveList = useSelector((state: StoreType) => state.okrObjective.objectiveList)
    const keyResultList = useSelector((state: StoreType) => state.okrKeyResult.keyResultList)

    const objective = useSelector((state: StoreType) => state.okrObjective.objectiveEdit)
    const reloadEdit = useSelector((state: StoreType) => state.okrObjective.reloadEdit)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    const reload = () => {
        dispatch(setOkrObjectiveReloadEdit(false))
        setTimeout(() => {
            dispatch(setOkrObjectiveReloadEdit(true))
        }, 0)
    }

    useEffect(() => {
        const params = new URLSearchParams(search)
        const parentObjectiveId = parseInt(params.get("parentObjectiveId"), 10) || null
        const parentKeyResultId = parseInt(params.get("parentKeyResultId"), 10) || null

        const promiseArray: Promise<any>[] = [
            // load sessions and their OKRs to set parent
            LoadAllOkrSessions(userData.accountId).then((res) => {
                // show only opened sessions
                const availableSessions = SortOkrSessions(Object.values(res), {
                    showArchived: false,
                    showInProgress: false,
                })
                availableSessions.sort((a, b) => sort(a, b, "name", ASC))
                dispatch(setOkrObjectiveAvailableSessions(availableSessions))

                const promiseNestedArray: Promise<any>[] = []
                const availableObjectives: { [sessionId: number]: OkrObjective[] } = {}
                const availableKeyResults: { [sessionId: number]: OkrKeyResult[] } = {}

                availableSessions.forEach((availableSession) => {
                    promiseNestedArray.push(
                        LoadAllOkrObjectives(availableSession.id).then((res1) => (
                            LoadAllOkrKeyResults(availableSession.id).then((res2) => {
                                const nested = PrepareOkrNested(res1.objectives, res2.keyResults)
                                const objectives: OkrObjective[] = []
                                const keyResults: OkrKeyResult[] = []

                                const addKeyResults = (nestedKeyResults: readonly KeyResultNested[]) => {
                                    nestedKeyResults.forEach((row) => {
                                        keyResults.push(row.keyResult)
                                        addObjectives(row.nestedObjectives)
                                    })
                                }
                                const addObjectives = (nestedObjectives: readonly ObjectiveNested[]) => {
                                    nestedObjectives.forEach((row) => {
                                        if (row.objective.id !== objectiveId) {
                                            objectives.push(row.objective)
                                            addObjectives(row.nestedObjectives)
                                            addKeyResults(row.nestedKeyResults)
                                        }
                                    })
                                }
                                addObjectives(nested)

                                availableObjectives[availableSession.id] = objectives.sort((a, b) => sort(a, b, "title", ASC))
                                availableKeyResults[availableSession.id] = keyResults.sort((a, b) => sort(a, b, "title", ASC))
                            })
                        )),
                    )
                })

                return Promise.all(promiseNestedArray).then(() => {
                    dispatch(setOkrObjectiveAvailableObjectives(availableObjectives))
                    dispatch(setOkrObjectiveAvailableKeyResults(availableKeyResults))
                })
            }),
        ]

        if (objectiveId === 0) {
            dispatch(setOkrObjectiveEdit({
                ...initOkrObjective,
                objectiveId: parentObjectiveId,
                objective: objectiveList[parentObjectiveId] || null,
                keyResultId: parentKeyResultId,
                keyResult: keyResultList[parentKeyResultId] || null,
            }))
        } else {
            promiseArray.push(
                LoadOkrObjectiveById(objectiveId, sessionId).then((res) => {
                    dispatch(setOkrObjectiveEdit(res))
                }),
            )
        }

        setLoadState(PENDING)
        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
            reload()
        })
    }, [])

    const isOwnerObjective = IsOwnerOkrObjective(objective, userData.userId)
    const isOwnerSession = session.owner === userData.userId
    const isOwner = isOwnerObjective || isOwnerSession

    const isOpenSession = session.status === OPEN

    const addAccess = hasAccess(PRM_OKR_OBJECTIVE_KEY_RESULT, ADD)
    const editAccess = hasAccess(PRM_OKR_OBJECTIVE_KEY_RESULT, EDIT)

    const allowedToAdd = (addAccess || isOwner) && isOpenSession
    const allowedToEdit = (editAccess || isOwner) && isOpenSession

    if (objectiveId === 0 && !allowedToAdd) return <AccessDenied />
    if (objectiveId !== 0 && !allowedToEdit) return <AccessDenied />
    if (!objective) return <NotFound />

    return (
        <Container>
            <PageHeader>
                <PageTitle>
                    {objective.id ? lang.editObjective : lang.addObjective}
                </PageTitle>
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                {
                    !reloadEdit
                    && <Form objective={objective} />
                }
                {
                    reloadEdit
                    && <Form objective={objective} />
                }
            </PageContent>
        </Container>
    )
}
export default ObjectiveEdit
